import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonCol, IonGrid, IonItem, IonLabel, IonRow, IonToggle, IonItemDivider, IonList } from '@ionic/react'
import { IRootState } from '../../../store'
import { VitalsConstants } from '../store/vitals.constants'
import { useTranslation } from 'react-i18next'
import '../page/Vitals.page.css'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import {
  saveVitals,
  setBloodPressureMethod,
  setBloodPressureMethodPNNV,
  setDiastolicBPPNNV,
  setEcgType,
  setEcgTypeMethod,
  setHeartRateMethod,
  setHeartRatePNNV,
  setRegularity,
  setRespirationRateEffort,
  setRespirationRatePNNV,
  setRhythm,
  setRhythmPNNV,
  setRoomAir,
  setShockIndex,
  setSpo2PNNV,
  setSystolicBPPNNV,
} from '../store/vitals.actions'
import { diastolicBPKeys, numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import Validation from '../../../components/validation/Validation.component'
import { getAllPNNVOptionsSelector, getVitalOptionsSelector } from '../../options/selectors'
import { IOption, IPNNVOption } from '../../../common/common.types'
import { IDataPointsConfig } from '../../configurations/store/configurations.types'
import { getDatapointsConfigSelector } from '../../configurations/selectors'

const BasicSection: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const vitals = useSelector((state: IRootState) => state.vitals)
  const {
    systolicBP,
    systolicBPPNNV,
    diastolicBP,
    diastolicBPPNNV,
    bloodPressureMethod,
    bloodPressureMethodPNNV,
    mapVital,
    shockIndex,
    heartRate,
    heartRatePNNV,
    heartRateMethod,
    spo2,
    spo2PNNV,
    roomAir,
    respirationRate,
    respirationRatePNNV,
    respirationRateEffort,
    rhythm,
    rhythmPNNV,
    regularity,
    ecgType,
    ecgTypeMethod,
    reRunValidations,
    key,
  } = vitals

  const vitalOptions = useSelector(getVitalOptionsSelector)
  const {
    bloodPressureMethodOptions,
    heartRateMethodOptions,
    respirationRateEffortOptions,
    ecgTypeOptions,
    ecgTypeMethodOptions,
    regularityOptions,
    rhythmOptions,
  } = vitalOptions

  const datapointsConfig: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_SHOCK_INDEX } = datapointsConfig

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const systolicBPPNNVOptions = allPNNVOptions?.['eVitals.06']
  const diastolicBPPNNVOptions = allPNNVOptions?.['eVitals.07']
  const heartRatePNNVOptions = allPNNVOptions?.['eVitals.10']
  const spo2PNNVOptions = allPNNVOptions?.['eVitals.12']
  const respirationRatePNNVOptions = allPNNVOptions?.['eVitals.14']

  const setMapVitalOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_MAP_VITAL,
      mapVital: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setShockIndexOnChange = (numbers: string[]) => {
    dispatch(setShockIndex(numbers.length ? numbers.join('') : null))
    dispatch(saveVitals())
  }

  const setHeartRateOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_HEART_RATE,
      heartRate: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setHeartRatePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setHeartRatePNNV(option))
    dispatch(saveVitals())
  }

  const setHeartRateMethodOnChange = (option: IOption) => {
    dispatch(setHeartRateMethod(option))
    dispatch(saveVitals())
  }

  const setRespirationRateOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_RESPIRATION_RATE,
      respirationRate: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setRespirationRatePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setRespirationRatePNNV(option))
    dispatch(saveVitals())
  }

  const setRespirationRateEffortOnChange = (option: IOption) => {
    dispatch(setRespirationRateEffort(option))
    dispatch(saveVitals())
  }

  const setSpo2OnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_SPO2,
      spo2: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setSpo2PNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setSpo2PNNV(option))
    dispatch(saveVitals())
  }

  const systolicBPOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_SYSTOLIC_BLOOD_PRESSURE,
      systolicBP: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const setSystolicBPPNNVOnChange = (systolicBPPNNV: IPNNVOption | null) => {
    dispatch(setSystolicBPPNNV(systolicBPPNNV))
    dispatch(saveVitals())
  }

  const setdiastolicBPPNNVOnChange = (diastolicBPPNNV: IPNNVOption | null) => {
    dispatch(setDiastolicBPPNNV(diastolicBPPNNV))
    dispatch(saveVitals())
  }

  const diastolicBPOnChange = (numbers: string[]) => {
    dispatch({
      type: VitalsConstants.SET_DIASTOLIC_BLOOD_PRESSURE,
      diastolicBP: numbers.length ? numbers.join('') : null,
    })
    dispatch(saveVitals())
  }

  const bloodPressureMethodOnChange = (option: IOption) => {
    dispatch(setBloodPressureMethod(option))
    dispatch(saveVitals())
  }

  const bloodPressureMethodPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setBloodPressureMethodPNNV(option))
    dispatch(saveVitals())
  }

  const roomAirOnChange = () => {
    dispatch(setRoomAir(!roomAir))
    dispatch(saveVitals())
  }

  const regularityOnChange = (option: IOption) => {
    dispatch(setRegularity(option))
    dispatch(saveVitals())
  }
  const ecgTypeOnChange = (option: IOption) => {
    dispatch(setEcgType(option))
    dispatch(saveVitals())
  }

  useEffect(() => {
    systolicBP &&
      dispatch({
        type: VitalsConstants.SET_SYSTOLIC_BLOOD_PRESSURE,
        systolicBP: systolicBP,
      })

    diastolicBP &&
      dispatch({
        type: VitalsConstants.SET_DIASTOLIC_BLOOD_PRESSURE,
        diastolicBP: diastolicBP,
      })

    mapVital &&
      dispatch({
        type: VitalsConstants.SET_MAP_VITAL,
        mapVital: mapVital,
      })

    shockIndex &&
      dispatch({
        type: VitalsConstants.SET_SHOCK_INDEX,
        shockIndex: shockIndex,
      })

    heartRate &&
      dispatch({
        type: VitalsConstants.SET_HEART_RATE,
        heartRate: heartRate,
      })

    respirationRate &&
      dispatch({
        type: VitalsConstants.SET_RESPIRATION_RATE,
        respirationRate: respirationRate,
      })

    spo2 &&
      dispatch({
        type: VitalsConstants.SET_SPO2,
        spo2: spo2,
      })
  }, [reRunValidations])

  return (
    <IonList>
      <IonItemDivider color='light' />
      <IonGrid className='ion-no-padding'>
        <IonRow id='SystolicBPDiastolicBP' className='mobile-nowrap'>
          <IonCol className='picklist'>
            <NumberPad
              dataTestId='SystolicBP'
              values={systolicBP ? systolicBP.split('') : []}
              label={t('vitals.basic.form.SystolicBP')}
              onNumPadClick={systolicBPOnChange}
              action={VitalsConstants.SET_SYSTOLIC_BLOOD_PRESSURE}
              pnnvAction={setSystolicBPPNNVOnChange}
              pnnvListHeader={t('vitals.basic.form.SystolicBP')}
              pnnvOptions={systolicBPPNNVOptions}
              pnnvValue={systolicBPPNNV}
            />
            <Validation id={VitalsConstants.SET_SYSTOLIC_BLOOD_PRESSURE} cardinalityKey={key} />
          </IonCol>

          <IonCol className='picklist'>
            <NumberPad
              dataTestId='DiastolicBP'
              values={diastolicBP ? diastolicBP.split('') : []}
              label={t('vitals.basic.form.DiastolicBP')}
              onNumPadClick={diastolicBPOnChange}
              validCharacters={diastolicBPKeys}
              action={VitalsConstants.SET_DIASTOLIC_BLOOD_PRESSURE}
              pnnvAction={setdiastolicBPPNNVOnChange}
              pnnvListHeader={t('vitals.basic.form.DiastolicBP')}
              pnnvOptions={diastolicBPPNNVOptions}
              pnnvValue={diastolicBPPNNV}
            />
            <Validation id={VitalsConstants.SET_DIASTOLIC_BLOOD_PRESSURE} cardinalityKey={key} />
          </IonCol>

          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='BPMethod'
              options={bloodPressureMethodOptions}
              selectedOption={bloodPressureMethod}
              listHeader={t('vitals.basic.form.Method')}
              label={t('vitals.basic.form.Method')}
              action={bloodPressureMethodOnChange}
              pnnvAction={bloodPressureMethodPNNVOnChange}
              selectedPNNVOption={bloodPressureMethodPNNV}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId='MapVital'
              dataTestId='MapVital'
              values={mapVital ? mapVital.split('') : []}
              label={t('vitals.basic.form.MAP')}
              onNumPadClick={setMapVitalOnChange}
              action={VitalsConstants.SET_MAP_VITAL}
              validCharacters={numberPadKeysWithDecimal}
            />
            <Validation id={VitalsConstants.SET_MAP_VITAL} cardinalityKey={key} />
          </IonCol>
          {(DP_SHOCK_INDEX === 1 || shockIndex) && (
            <IonCol className='picklist'>
              <NumberPad
                elementId='ShockIndex'
                dataTestId='ShockIndex'
                values={shockIndex ? shockIndex.split('') : []}
                label={t('vitals.basic.form.ShockIndex')}
                onNumPadClick={setShockIndexOnChange}
                action={VitalsConstants.SET_SHOCK_INDEX}
                validCharacters={numberPadKeysWithDecimal}
              />
            </IonCol>
          )}
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId='HeartRate'
              dataTestId='HeartRate'
              values={heartRate ? heartRate.split('') : []}
              label={t('vitals.basic.form.HeartRate')}
              onNumPadClick={setHeartRateOnChange}
              action={VitalsConstants.SET_HEART_RATE}
              validCharacters={numberPadKeysWithDecimal}
              pnnvAction={setHeartRatePNNVOnChange}
              pnnvListHeader={t('vitals.basic.form.HeartRate')}
              pnnvOptions={heartRatePNNVOptions}
              pnnvValue={heartRatePNNV}
            />
            <Validation id={VitalsConstants.SET_HEART_RATE} cardinalityKey={key} />
          </IonCol>

          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='HRMethod'
              options={heartRateMethodOptions}
              selectedOption={heartRateMethod}
              listHeader={t('vitals.basic.form.Method')}
              label={t('vitals.basic.form.Method')}
              action={setHeartRateMethodOnChange}
            />
          </IonCol>
        </IonRow>
      </IonGrid>

      <IonItemDivider color='light' />

      <IonGrid className='ion-no-padding'>
        <IonRow className='mobile-nowrap'>
          <IonCol className='picklist'>
            <NumberPad
              elementId='RespiratoryRate'
              dataTestId='RespiratoryRate'
              values={respirationRate ? respirationRate.split('') : []}
              label={t('vitals.basic.form.RespiratoryRate')}
              onNumPadClick={setRespirationRateOnChange}
              action={VitalsConstants.SET_RESPIRATION_RATE}
              validCharacters={numberPadKeysWithDecimal}
              pnnvAction={setRespirationRatePNNVOnChange}
              pnnvListHeader={t('vitals.basic.form.RespiratoryRate')}
              pnnvOptions={respirationRatePNNVOptions}
              pnnvValue={respirationRatePNNV}
            />
            <Validation id={VitalsConstants.SET_RESPIRATION_RATE} cardinalityKey={key} />
          </IonCol>

          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='RRMethod'
              options={respirationRateEffortOptions}
              selectedOption={respirationRateEffort}
              listHeader={t('vitals.basic.form.Effort')}
              label={t('vitals.basic.form.Effort')}
              action={setRespirationRateEffortOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId='SPO2'
              dataTestId='SPO2'
              values={spo2 ? spo2.split('') : []}
              label={t('vitals.basic.form.SPO2')}
              onNumPadClick={setSpo2OnChange}
              action={VitalsConstants.SET_SPO2}
              validCharacters={numberPadKeysWithDecimal}
              pnnvAction={setSpo2PNNVOnChange}
              pnnvListHeader={t('vitals.basic.form.SPO2')}
              pnnvOptions={spo2PNNVOptions}
              pnnvValue={spo2PNNV}
            />
            <Validation id={VitalsConstants.SET_SPO2} cardinalityKey={key} />
          </IonCol>

          <IonCol className='picklist'>
            <IonItem lines='none'>
              <IonLabel>{t('vitals.basic.form.RoomAir')}</IonLabel>
              <IonToggle
                slot='start'
                checked={roomAir}
                onClick={() => {
                  roomAirOnChange()
                }}
              />
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>

      <IonItemDivider color='light' />

      <IonGrid className='ion-no-padding'>
        <IonRow>
          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='Rhythm'
              listHeader={t('vitals.basic.form.Rhythm')}
              options={rhythmOptions}
              savedValues={rhythm}
              action={setRhythm}
              callback={saveVitals}
              label={t('vitals.basic.form.Rhythm')}
              pnnvAction={setRhythmPNNV}
              savedPNNVValue={rhythmPNNV}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='RegularityOptions'
              options={regularityOptions}
              selectedOption={regularity}
              listHeader={t('vitals.basic.form.Regularity')}
              label={t('vitals.basic.form.Regularity')}
              action={regularityOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='EcgTypeOptions'
              options={ecgTypeOptions}
              selectedOption={ecgType}
              listHeader={t('vitals.basic.form.ECGType')}
              label={t('vitals.basic.form.ECGType')}
              action={ecgTypeOnChange}
            />
          </IonCol>

          <IonCol className='picklist'>
            <MultiSelect
              dataTestID='EcgTypeMethod'
              listHeader={t('vitals.basic.form.Method')}
              options={ecgTypeMethodOptions}
              savedValues={ecgTypeMethod}
              label={t('vitals.basic.form.Method')}
              action={setEcgTypeMethod}
              callback={saveVitals}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonList>
  )
}

export default BasicSection
